import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BomberosHeader } from '../../components/BomberosHeader';
import { bomberosEstadisticasAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
    ArrowLeft, BarChart3, Users, GraduationCap, Truck, Shield, Calendar,
    RefreshCw, CheckCircle, Clock, AlertTriangle, XCircle, ChevronDown, ChevronUp,
} from 'lucide-react';

const ESTADO_CONFIG = {
    finalizada: { label: 'Finalizada', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    en_curso: { label: 'En Curso', color: 'bg-blue-100 text-blue-700', icon: Clock },
    programada: { label: 'Programada', color: 'bg-amber-100 text-amber-700', icon: Clock },
};

const ESTADO_VEH = {
    operativo: { color: 'text-green-600 bg-green-50', label: 'Operativo' },
    mantenimiento: { color: 'text-amber-600 bg-amber-50', label: 'Mantención' },
    fuera_servicio: { color: 'text-red-600 bg-red-50', label: 'Fuera Servicio' },
};

function PctBadge({ value, size = 'sm' }) {
    const cls = value >= 80 ? 'bg-green-100 text-green-700' :
                value >= 50 ? 'bg-amber-100 text-amber-700' :
                value > 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500';
    return (
        <span className={`inline-flex items-center rounded-md px-2 py-0.5 font-semibold ${cls} ${size === 'lg' ? 'text-base' : 'text-xs'}`}>
            {value}%
        </span>
    );
}

function KpiCard({ icon: Icon, label, value, sub, color }) {
    return (
        <Card className="border" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="p-4">
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${color}`}>
                        <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-2xl font-bold leading-none">{value}</p>
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">{label}</p>
                        {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

function CapRow({ cap, defaultOpen = false }) {
    const [open, setOpen] = useState(defaultOpen);
    const cfg = ESTADO_CONFIG[cap.estado] || ESTADO_CONFIG.programada;
    const Icon = cfg.icon;

    return (
        <div className="border rounded-lg overflow-hidden" data-testid={`cap-row-${cap.nombre}`}>
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center gap-2 p-3 hover:bg-muted/50 transition-colors text-left"
            >
                <Icon className={`h-4 w-4 shrink-0 ${cfg.color.includes('green') ? 'text-green-600' : cfg.color.includes('blue') ? 'text-blue-600' : 'text-amber-600'}`} />
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{cap.nombre}</p>
                    <p className="text-[10px] text-muted-foreground">
                        {cap.fecha_inicio ? new Date(cap.fecha_inicio).toLocaleDateString('es-CL') : 'Sin fecha'}
                    </p>
                </div>
                <Badge className={`${cfg.color} text-[10px] border-0 shrink-0`}>{cfg.label}</Badge>
                <div className="text-right shrink-0 min-w-[70px]">
                    <p className="text-sm font-bold">{cap.asistieron}/{cap.convocados}</p>
                    <PctBadge value={cap.porcentaje} />
                </div>
                {cap.por_compania?.length > 0 && (
                    open ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                )}
            </button>
            {open && cap.por_compania?.length > 0 && (
                <div className="border-t bg-muted/30 px-3 py-2 space-y-1">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Desglose por compania</p>
                    {cap.por_compania.map((c, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs">
                            <span className="w-12 font-medium text-muted-foreground">{c.compania}</span>
                            <Progress value={c.porcentaje} className="flex-1 h-2" />
                            <span className="w-16 text-right tabular-nums">{c.asistieron}/{c.convocados}</span>
                            <PctBadge value={c.porcentaje} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function BomberosEstadisticasPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const res = await bomberosEstadisticasAPI.getDetallado();
            setData(res.data);
        } catch (err) {
            console.error('Error:', err);
            toast.error('Error al cargar estadísticas');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-4 p-4 md:p-6">
                <Skeleton className="h-10 w-64" />
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
                </div>
                <Skeleton className="h-96" />
            </div>
        );
    }

    if (!data) return null;

    const { capacitaciones: caps, personal, material_mayor: mat, epp, actas } = data;

    return (
        <>
            <BomberosHeader />
            <div className="space-y-5 p-4 md:p-6 pt-20 md:pt-20">
                {/* Header */}
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/dashboard')} data-testid="back-btn">
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div>
                            <h1 className="text-lg sm:text-xl font-bold flex items-center gap-2">
                                <BarChart3 className="h-5 w-5 text-indigo-500" />
                                Estadisticas Detalladas
                            </h1>
                            <p className="text-xs text-muted-foreground">Datos concretos del cuerpo de bomberos</p>
                        </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-btn">
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                </div>

                {/* Global KPIs */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="kpi-grid">
                    <KpiCard icon={GraduationCap} label="Capacitaciones" value={caps.total}
                        sub={`${caps.finalizadas} finalizadas, ${caps.en_curso} en curso`}
                        color="bg-purple-100 text-purple-700" />
                    <KpiCard icon={Users} label="Bomberos Activos" value={personal.total_activos}
                        sub={personal.total_inactivos > 0 ? `${personal.total_inactivos} inactivos` : 'Todos activos'}
                        color="bg-blue-100 text-blue-700" />
                    <KpiCard icon={Truck} label="Vehiculos Operativos" value={`${mat.operativos}/${mat.total}`}
                        sub={mat.fuera_servicio > 0 ? `${mat.fuera_servicio} fuera de servicio` : 'Todos operativos'}
                        color="bg-green-100 text-green-700" />
                    <KpiCard icon={Shield} label="EPP al Dia" value={`${epp.al_dia}/${epp.total}`}
                        sub={epp.vencidos > 0 ? `${epp.vencidos} vencidos, ${epp.por_vencer_30_dias} por vencer` : 'Todo al dia'}
                        color={epp.vencidos > 0 ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"} />
                    <KpiCard icon={Calendar} label="Actas / Actividades" value={actas?.total || 0}
                        sub={actas?.registrados > 0 ? `${actas.presentes}/${actas.registrados} presentes (${actas.porcentaje_asistencia}%)` : 'Sin registros'}
                        color="bg-orange-100 text-orange-700" />
                </div>

                {/* Asistencia Global Banner */}
                {caps.asistencia_global.convocados > 0 && (
                    <Card className="border-2 border-indigo-200 bg-gradient-to-r from-indigo-50 to-white" data-testid="asistencia-global">
                        <CardContent className="p-4">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                <div className="flex-1">
                                    <p className="text-sm font-semibold text-indigo-900">Asistencia Global a Capacitaciones</p>
                                    <p className="text-xs text-indigo-600 mt-0.5">
                                        {caps.asistencia_global.asistieron} asistieron de {caps.asistencia_global.convocados} convocados
                                    </p>
                                </div>
                                <div className="flex items-center gap-3 flex-1">
                                    <Progress value={caps.asistencia_global.porcentaje} className="flex-1 h-3" />
                                    <PctBadge value={caps.asistencia_global.porcentaje} size="lg" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Tabs */}
                <Tabs defaultValue="capacitaciones" className="space-y-4">
                    <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                        <TabsList className="inline-flex w-auto min-w-full sm:grid sm:grid-cols-4 sm:w-full sm:max-w-xl">
                            <TabsTrigger value="capacitaciones" className="text-xs sm:text-sm whitespace-nowrap gap-1">
                                <GraduationCap className="h-3.5 w-3.5" /> Capacitaciones
                            </TabsTrigger>
                            <TabsTrigger value="personal" className="text-xs sm:text-sm whitespace-nowrap gap-1">
                                <Users className="h-3.5 w-3.5" /> Personal
                            </TabsTrigger>
                            <TabsTrigger value="material" className="text-xs sm:text-sm whitespace-nowrap gap-1">
                                <Truck className="h-3.5 w-3.5" /> Material Mayor
                            </TabsTrigger>
                            <TabsTrigger value="epp" className="text-xs sm:text-sm whitespace-nowrap gap-1">
                                <Shield className="h-3.5 w-3.5" /> EPP
                            </TabsTrigger>
                        </TabsList>
                    </div>

                    {/* TAB: Capacitaciones */}
                    <TabsContent value="capacitaciones" className="space-y-4">
                        {/* Asistencia por compañía */}
                        <Card data-testid="asistencia-por-cia">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm">Asistencia por Compania</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                {caps.por_compania.map((c, i) => (
                                    <div key={i} className="flex items-center gap-2 text-sm">
                                        <span className="w-20 sm:w-32 font-medium truncate" title={c.compania_nombre}>{c.compania}</span>
                                        <Progress value={c.porcentaje} className="flex-1 h-3" />
                                        <span className="w-20 text-right text-xs tabular-nums text-muted-foreground">
                                            {c.asistieron}/{c.convocados}
                                        </span>
                                        <PctBadge value={c.porcentaje} />
                                    </div>
                                ))}
                            </CardContent>
                        </Card>

                        {/* Detalle capacitaciones */}
                        <Card data-testid="detalle-caps">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm">Detalle de Capacitaciones ({caps.total})</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                {caps.detalle.length === 0 ? (
                                    <p className="text-sm text-muted-foreground text-center py-4">No hay capacitaciones registradas</p>
                                ) : (
                                    caps.detalle.map((cap, i) => <CapRow key={i} cap={cap} />)
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* TAB: Personal */}
                    <TabsContent value="personal" className="space-y-4">
                        <Card data-testid="personal-por-cia">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm">Dotacion por Compania</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {personal.por_compania.map((c, i) => (
                                        <div key={i} className="flex items-center gap-3">
                                            <span className="w-20 sm:w-32 text-sm font-medium truncate" title={c.compania_nombre}>{c.compania}</span>
                                            <div className="flex-1">
                                                <Progress value={c.porcentaje_del_total} className="h-4" />
                                            </div>
                                            <div className="text-right shrink-0 min-w-[90px]">
                                                <span className="text-sm font-bold">{c.activos}</span>
                                                <span className="text-xs text-muted-foreground ml-1">activos</span>
                                                {c.inactivos > 0 && (
                                                    <span className="text-[10px] text-red-500 block">+{c.inactivos} inactivos</span>
                                                )}
                                            </div>
                                            <span className="text-xs text-muted-foreground w-10 text-right">{c.porcentaje_del_total}%</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-4 pt-3 border-t flex items-center justify-between text-sm">
                                    <span className="font-semibold">Total</span>
                                    <span className="font-bold text-lg">{personal.total_activos} bomberos activos</span>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* TAB: Material Mayor */}
                    <TabsContent value="material" className="space-y-4">
                        {mat.por_compania.map((c, i) => (
                            <Card key={i} data-testid={`mat-cia-${c.compania}`}>
                                <CardHeader className="pb-2">
                                    <div className="flex items-center justify-between">
                                        <CardTitle className="text-sm">{c.compania_nombre}</CardTitle>
                                        <div className="flex gap-1">
                                            <Badge variant="outline" className="text-green-600 text-[10px]">{c.operativos} op.</Badge>
                                            {c.fuera_servicio > 0 && (
                                                <Badge variant="outline" className="text-red-600 text-[10px]">{c.fuera_servicio} f/s</Badge>
                                            )}
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    {c.vehiculos.length === 0 ? (
                                        <p className="text-xs text-muted-foreground">Sin vehiculos asignados</p>
                                    ) : (
                                        <div className="space-y-1">
                                            {c.vehiculos.map((v, j) => {
                                                const estCfg = ESTADO_VEH[v.estado] || { color: 'text-gray-600 bg-gray-50', label: v.estado };
                                                return (
                                                    <div key={j} className="flex items-center justify-between py-1 px-2 rounded hover:bg-muted/50">
                                                        <div className="flex items-center gap-2">
                                                            <Truck className="h-3.5 w-3.5 text-muted-foreground" />
                                                            <span className="text-sm font-medium">{v.codigo}</span>
                                                            <span className="text-xs text-muted-foreground">{v.marca} {v.modelo}</span>
                                                        </div>
                                                        <Badge className={`${estCfg.color} text-[10px] border-0`}>{estCfg.label}</Badge>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                        <div className="text-center py-2">
                            <p className="text-sm text-muted-foreground">
                                Total: <span className="font-bold">{mat.total}</span> vehiculos 
                                — <span className="text-green-600 font-medium">{mat.operativos} operativos</span>
                                {mat.fuera_servicio > 0 && (
                                    <> — <span className="text-red-600 font-medium">{mat.fuera_servicio} fuera de servicio</span></>
                                )}
                            </p>
                        </div>
                    </TabsContent>

                    {/* TAB: EPP */}
                    <TabsContent value="epp" className="space-y-4">
                        {/* Summary */}
                        <div className="grid grid-cols-3 gap-3">
                            <Card className="border-green-200 bg-green-50/50">
                                <CardContent className="p-3 text-center">
                                    <CheckCircle className="h-5 w-5 mx-auto text-green-600 mb-1" />
                                    <p className="text-xl font-bold text-green-700">{epp.al_dia}</p>
                                    <p className="text-[10px] text-green-600">Al dia</p>
                                </CardContent>
                            </Card>
                            <Card className="border-amber-200 bg-amber-50/50">
                                <CardContent className="p-3 text-center">
                                    <AlertTriangle className="h-5 w-5 mx-auto text-amber-600 mb-1" />
                                    <p className="text-xl font-bold text-amber-700">{epp.por_vencer_30_dias}</p>
                                    <p className="text-[10px] text-amber-600">Por vencer (30d)</p>
                                </CardContent>
                            </Card>
                            <Card className="border-red-200 bg-red-50/50">
                                <CardContent className="p-3 text-center">
                                    <XCircle className="h-5 w-5 mx-auto text-red-600 mb-1" />
                                    <p className="text-xl font-bold text-red-700">{epp.vencidos}</p>
                                    <p className="text-[10px] text-red-600">Vencidos</p>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Vencidos */}
                        {epp.detalle_vencidos.length > 0 && (
                            <Card className="border-red-200" data-testid="epp-vencidos">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm text-red-700 flex items-center gap-2">
                                        <XCircle className="h-4 w-4" /> EPP Vencidos ({epp.vencidos})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-1">
                                        {epp.detalle_vencidos.map((e, i) => (
                                            <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-red-50/50 text-sm">
                                                <div className="min-w-0 flex-1">
                                                    <p className="font-medium truncate">{e.bombero}</p>
                                                    <p className="text-[10px] text-muted-foreground">{e.tipo_epp} — {e.compania}</p>
                                                </div>
                                                <Badge variant="destructive" className="text-[10px] shrink-0">
                                                    {Math.abs(e.dias_restantes)}d vencido
                                                </Badge>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Por vencer */}
                        {epp.detalle_por_vencer.length > 0 && (
                            <Card className="border-amber-200" data-testid="epp-por-vencer">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm text-amber-700 flex items-center gap-2">
                                        <AlertTriangle className="h-4 w-4" /> Por Vencer ({epp.por_vencer_30_dias})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-1">
                                        {epp.detalle_por_vencer.map((e, i) => (
                                            <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-amber-50/50 text-sm">
                                                <div className="min-w-0 flex-1">
                                                    <p className="font-medium truncate">{e.bombero}</p>
                                                    <p className="text-[10px] text-muted-foreground">{e.tipo_epp} — {e.compania}</p>
                                                </div>
                                                <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px] shrink-0">
                                                    {e.dias_restantes}d restantes
                                                </Badge>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {epp.detalle_vencidos.length === 0 && epp.detalle_por_vencer.length === 0 && (
                            <Card>
                                <CardContent className="p-8 text-center">
                                    <CheckCircle className="h-10 w-10 mx-auto text-green-500 mb-2" />
                                    <p className="text-sm font-medium">Todo el EPP esta al dia</p>
                                    <p className="text-xs text-muted-foreground">No hay equipos vencidos ni por vencer</p>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </>
    );
}
